class CardVariantSwitcher {
  constructor() {
    this.imageCache = new Map();
    this.handleSwatchInteraction = this.handleSwatchInteraction.bind(this);
    this.handleSwatchKeydown = this.handleSwatchKeydown.bind(this);
    this.init();
    this.preloadImages();
    this.setupDynamicContentListener();
    this.syncAllSwatchStates();
  }

  setupDynamicContentListener() {
    document.addEventListener('product-recommendations-loaded', () => {
      this.preloadImages();
      this.syncAllSwatchStates();
    });
  }

  getSwatchLabel(swatch) {
    if (!swatch.dataset.swatchLabel) {
      const initialLabel = swatch.getAttribute('aria-label') || swatch.title || swatch.textContent.trim();
      swatch.dataset.swatchLabel = initialLabel.replace(/, current color$/i, '').trim();
    }

    return swatch.dataset.swatchLabel;
  }

  syncAllSwatchStates() {
    document.querySelectorAll('.card-wrapper').forEach((card) => {
      card.querySelectorAll('.card__color-swatch').forEach((swatch) => this.getSwatchLabel(swatch));
      const activeSwatch = card.querySelector('.card__color-swatch.active');
      this.updateSwatchActiveState(card, activeSwatch);
    });
  }

  updateSwatchActiveState(card, activeSwatch) {
    card.querySelectorAll('.card__color-swatch').forEach((swatch) => {
      const baseLabel = this.getSwatchLabel(swatch);
      swatch.classList.remove('active');
      swatch.removeAttribute('aria-current');
      swatch.setAttribute('aria-label', baseLabel);
    });

    if (!activeSwatch) return;

    const activeLabel = this.getSwatchLabel(activeSwatch);
    activeSwatch.classList.add('active');
    activeSwatch.setAttribute('aria-current', 'true');
    activeSwatch.setAttribute('aria-label', `${activeLabel}, current color`);
  }

  init() {
    document.addEventListener('click', this.handleSwatchInteraction, { passive: false });
    document.addEventListener('keydown', this.handleSwatchKeydown);
    document.addEventListener(
      'mouseenter',
      (e) => {
        let swatch = null;
        if (e.target && typeof e.target.closest === 'function') {
          swatch = e.target.closest('.card__color-swatch');
        }
        if (!swatch || swatch.classList.contains('active')) return;

        const variantImage = swatch.dataset.variantImage;
        if (variantImage && !this.imageCache.has(variantImage)) {
          this.preloadImage(variantImage);
        }
      },
      true
    );
  }

  handleSwatchKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const swatch = event.target?.closest('.card__color-swatch');
    if (!swatch || swatch.tagName === 'A') return;

    event.preventDefault();
    this.selectSwatch(swatch);
  }

  handleSwatchInteraction(event) {
    const swatch = event.target?.closest('.card__color-swatch');
    if (!swatch || swatch.tagName === 'A') return;

    event.preventDefault();
    event.stopPropagation();
    this.selectSwatch(swatch);
  }

  selectSwatch(swatch) {
    const card = swatch.closest('.card-wrapper');
    if (!card || swatch.classList.contains('active')) return;

    const variantId = swatch.dataset.variantId;
    const variantImage = swatch.dataset.variantImage;
    const variantImageWidth = swatch.dataset.variantImageWidth;
    const variantImageHeight = swatch.dataset.variantImageHeight;
    const variantImageAlt = swatch.dataset.variantImageAlt;

    if (!variantImage) return;

    requestAnimationFrame(() => {
      this.updateCardImage(card, {
        url: variantImage,
        width: variantImageWidth,
        height: variantImageHeight,
        alt: variantImageAlt,
      });

      card.classList.add('variant-selected');
      this.updateSwatchActiveState(card, swatch);
      this.updateProductLinks(card, variantId);
    });
  }

  preloadImages() {
    document.querySelectorAll('.card__color-swatch[data-variant-image]').forEach((swatch) => {
      const imageUrl = swatch.dataset.variantImage;
      if (imageUrl) {
        this.preloadImage(imageUrl);
      }
    });
  }

  preloadImage(url) {
    if (this.imageCache.has(url)) return;

    const img = new Image();
    img.src = this.getImageUrl(url, 533);
    this.imageCache.set(url, img);
  }

  updateCardImage(card, imageData) {
    const primaryImage = card.querySelector('.card__media .media img:first-child');
    if (!primaryImage) return;

    const cachedImage = this.imageCache.get(imageData.url);
    const newSrc = this.getImageUrl(imageData.url, 533);
    const srcset = this.generateSrcset(imageData.url, imageData.width);

    primaryImage.srcset = srcset;
    primaryImage.src = newSrc;
    primaryImage.alt = imageData.alt || '';
    primaryImage.width = imageData.width;
    primaryImage.height = imageData.height;

    if (cachedImage && cachedImage.complete) {
      primaryImage.style.opacity = '1';
    }
  }

  generateSrcset(baseUrl, maxWidth) {
    const widths = [165, 360, 533, 720, 940, 1066];
    const srcsetParts = [];

    widths.forEach((width) => {
      if (maxWidth >= width) {
        srcsetParts.push(`${this.getImageUrl(baseUrl, width)} ${width}w`);
      }
    });

    srcsetParts.push(`${baseUrl} ${maxWidth}w`);

    return srcsetParts.join(', ');
  }

  getImageUrl(baseUrl, width) {
    const url = baseUrl.split('?')[0];
    return `${url}?width=${width}`;
  }

  updateProductLinks(card, variantId) {
    card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const url = new URL(link.href);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CardVariantSwitcher();
  });
} else {
  new CardVariantSwitcher();
}
